import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box } from '@chakra-ui/react';
import LoadingIcon from 'studio_react/common/components/LoadingIcon';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import 'video.js/dist/video-js.css';
import { useTranslate } from 'ts/common/hooks';
import VideoControls from './VideoControls';
import VideoInfo from './VideoInfo';
import useVideoPlayer from './useVideoPlayer';

export interface VideoPlayerHandle {
    getCurrentTime: () => number;
    play: () => void;
    stop: () => void;
}

export interface VideoPlayerState {
    hasStarted: boolean;
    isEnded: boolean;
}

interface Props {
    url: string;
    thumbnailUrl?: string;
    fileName?: string;
    fileSize?: string;
    onClose?: () => void;
    onPlayStateChange?: (state: VideoPlayerState) => void;
    spEventBus?: SpAngularJs.ISpEventBus;
}

const VideoPlayer = forwardRef<VideoPlayerHandle, Props>(function VideoPlayer(
    { url, thumbnailUrl, fileName, fileSize, onClose, onPlayStateChange, spEventBus },
    ref
) {
    const {
        containerRef,
        videoRef,
        hasStarted,
        isEnded,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        isFullscreen,
        showSettings,
        playbackSpeed,
        quality,
        qualities,
        handleFocus,
        handleBlur,
        handlePlayPause,
        handleSeek,
        handleToggleMute,
        handleVolumeChange,
        handleToggleFullscreen,
        handleSpeedChange,
        handleQualityChange,
        handleReplay,
        toggleSettings,
        getCurrentTime,
        play,
        stop
    } = useVideoPlayer({ url, thumbnailUrl, onClose, spEventBus });
    const t = useTranslate('studio.common.videoPlayer');
    const [isHovered, setIsHovered] = useState(false);
    const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(!thumbnailUrl);

    useEffect(() => {
        if (!thumbnailUrl) {
            setIsThumbnailLoaded(true);
            return;
        }

        setIsThumbnailLoaded(false);
        let cancelled = false;

        const img = new Image();

        const done = () => {
            if (!cancelled) {
                setIsThumbnailLoaded(true);
            }
        };

        img.onload = done;
        img.onerror = done;
        img.src = thumbnailUrl;

        return () => {
            cancelled = true;
        };
    }, [thumbnailUrl]);

    useImperativeHandle(ref, () => ({ getCurrentTime, play, stop }), [getCurrentTime, play, stop]);

    useEffect(() => {
        onPlayStateChange?.({ hasStarted, isEnded });
    }, [hasStarted, isEnded, onPlayStateChange]);

    return (
        <>
            <Box
                ref={containerRef}
                position="relative"
                w="100%"
                tabIndex={0}
                outline="none"
                onFocus={handleFocus}
                onBlur={handleBlur}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    '& .vjs-tech': { objectFit: 'contain' },
                    ...(isFullscreen && {
                        height: '100vh',
                        bg: 'black',
                        '& .video-js': {
                            height: '100vh !important',
                            paddingTop: '0 !important'
                        }
                    })
                }}
            >
                <div ref={videoRef} />

                {/* Transparent click-to-play overlay — above video.js element, below controls */}
                {!isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        zIndex={1}
                        cursor="pointer"
                        onClick={handlePlayPause}
                    />
                )}

                {!hasStarted && !isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        zIndex={2}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        pointerEvents="none"
                        backgroundImage={
                            thumbnailUrl && isThumbnailLoaded ? `url(${thumbnailUrl})` : undefined
                        }
                        backgroundSize="contain"
                        backgroundRepeat="no-repeat"
                        backgroundPosition="center"
                        bg={thumbnailUrl && isThumbnailLoaded ? undefined : 'black'}
                    >
                        {thumbnailUrl && !isThumbnailLoaded ? (
                            <LoadingIcon width="64px" height="64px" />
                        ) : (
                            <Box
                                w="100px"
                                h="100px"
                                borderRadius="full"
                                bg="white"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                boxShadow="0px 10px 32px 0px rgba(0,0,0,0.14)"
                                color="primary.400"
                                pl="12px"
                            >
                                <PlayIcon width={64} height={64} />
                            </Box>
                        )}
                    </Box>
                )}

                {/* Custom controls overlay — visible on hover or when paused, after first play */}
                {hasStarted && !isEnded && (isHovered || !isPlaying) && (
                    <VideoControls
                        isPlaying={isPlaying}
                        currentTime={currentTime}
                        duration={duration}
                        volume={volume}
                        isMuted={isMuted}
                        isFullscreen={isFullscreen}
                        showSettings={showSettings}
                        playbackSpeed={playbackSpeed}
                        quality={quality}
                        qualities={qualities}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onToggleMute={handleToggleMute}
                        onVolumeChange={handleVolumeChange}
                        onToggleFullscreen={handleToggleFullscreen}
                        onToggleSettings={toggleSettings}
                        onSpeedChange={handleSpeedChange}
                        onQualityChange={handleQualityChange}
                    />
                )}

                {/* Ended overlay — thumbnail + replay button */}
                {isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        backgroundImage={
                            thumbnailUrl && isThumbnailLoaded ? `url(${thumbnailUrl})` : undefined
                        }
                        backgroundSize="contain"
                        backgroundRepeat="no-repeat"
                        backgroundPosition="center"
                        bg={thumbnailUrl && isThumbnailLoaded ? undefined : 'black'}
                    >
                        {thumbnailUrl && !isThumbnailLoaded ? (
                            <LoadingIcon width="64px" height="64px" />
                        ) : (
                            <Box
                                as="button"
                                onClick={handleReplay}
                                aria-label={t('replay')}
                                bg="blackAlpha.700"
                                border="2px solid"
                                borderColor="whiteAlpha.800"
                                borderRadius="full"
                                w={16}
                                h={16}
                                cursor="pointer"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color="white"
                                fontSize="2xl"
                                lineHeight={1}
                            >
                                ↺
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Filename + filesize — below video, hidden in fullscreen */}
            {!isFullscreen && <VideoInfo fileName={fileName} fileSize={fileSize} />}
        </>
    );
});

export default VideoPlayer;
